import type { IPublicPersonListItem } from '@skystone/shared';
import { DataTable, type ColumnDef } from '@/components/ui';

interface PublicPeopleTableProps {
  items: IPublicPersonListItem[];
}

function initials(displayName: string): string {
  const trimmed = displayName.trim();
  if (trimmed.length === 0) return '?';
  const parts = trimmed.split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('');
}

// Cell renderers are hoisted to module scope (S6478): defining them inside the
// component would create a new function identity on every render.
function renderDisplayNameCell(_value: unknown, row: IPublicPersonListItem) {
  return (
    <span className="flex items-center gap-3 font-medium text-foreground">
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
        {initials(row.displayName)}
      </span>
      <span className="truncate">{row.displayName}</span>
    </span>
  );
}

function renderEmailCell(_value: unknown, row: IPublicPersonListItem) {
  return <span className="text-muted-foreground">{row.primaryEmail ?? '—'}</span>;
}

function renderDepartmentCell(_value: unknown, row: IPublicPersonListItem) {
  return <span>{row.department ?? '—'}</span>;
}

function renderJobTitleCell(_value: unknown, row: IPublicPersonListItem) {
  return <span>{row.jobTitle ?? '—'}</span>;
}

function renderManagerCell(_value: unknown, row: IPublicPersonListItem) {
  return <span>{row.managerName ?? '—'}</span>;
}

const PUBLIC_PEOPLE_COLUMNS: ColumnDef<IPublicPersonListItem>[] = [
  {
    id: 'displayName',
    header: 'Display name',
    accessor: (row) => row.displayName,
    sortable: true,
    filter: { type: 'text', placeholder: 'Filter name…' },
    cell: renderDisplayNameCell,
  },
  {
    id: 'primaryEmail',
    header: 'Email',
    accessor: (row) => row.primaryEmail ?? '',
    sortable: true,
    filter: { type: 'text', placeholder: 'Filter email…' },
    cell: renderEmailCell,
  },
  {
    id: 'department',
    header: 'Department',
    accessor: (row) => row.department ?? '',
    sortable: true,
    filter: { type: 'text', placeholder: 'Filter department…' },
    cell: renderDepartmentCell,
  },
  {
    id: 'jobTitle',
    header: 'Job title',
    accessor: (row) => row.jobTitle ?? '',
    sortable: true,
    filter: { type: 'text', placeholder: 'Filter title…' },
    cell: renderJobTitleCell,
  },
  {
    id: 'managerName',
    header: 'Manager',
    accessor: (row) => row.managerName ?? '',
    sortable: true,
    filter: { type: 'text', placeholder: 'Filter manager…' },
    cell: renderManagerCell,
  },
];

// Read-only People directory table (PRODOPSAI-491). No Kind / Linked / Status
// columns and no row actions — purely informational, all-users.
export function PublicPeopleTable({ items }: Readonly<PublicPeopleTableProps>) {
  return <DataTable data={items} columns={PUBLIC_PEOPLE_COLUMNS} keyExtractor={(row) => row.id} />;
}
